<script lang="ts" setup>
  import { useCssModule } from 'vue';

  import { t } from '../../locale';
  import type { VignetteNames } from '../Illustration/Illustration.models';
  import Illustration from '../Illustration/Illustration.vue';

  export interface EmptyStateProps {
    /**
     * @deprecated Use illustration instead.
     */
    image?: {
      alt?: string;
      src: string;
    };

    /**
     * @deprecated Use title instead.
     */
    text?: string;

    /**
     * the title text of the empty state
     */
    title?: string;

    /**
     * the subtitle text of the empty state
     */
    subtitle?: string;

    /**
     * additional text to place at the bottom of the empty state
     */
    footnote?: string;

    /**
     * The name of the vignette illustration to render
     */
    vignette?: VignetteNames;

    /**
     * The size in pixels of the vignette illustration. Defaults to 300.
     */
    vignetteSize?: number;
  }

  export interface EmptyStateSlots {
    title?: () => unknown;
    subtitle?: () => unknown;
    footnote?: () => unknown;
    button?: () => unknown;
    image?: () => unknown;
  }

  defineOptions({
    name: 'll-empty-state',
  });

  const props = withDefaults(defineProps<EmptyStateProps>(), {
    /**
     * An optional image to render
     */
    image: () => ({
      alt: '',
      src: '',
    }),

    /**
     * The custom message to render
     */
    text: '',

    title: '',
    subtitle: '',
    footnote: '',
    vignette: undefined,
    vignetteSize: 300,
  });

  const classes = useCssModule();
  const slots = defineSlots<EmptyStateSlots>();
</script>

<template>
  <div key="empty" class="stash-empty-state" data-test="stash-empty-state">
    <template v-if="props.title || slots.title || props.subtitle || slots.subtitle || props.vignette">
      <div class="flex flex-col items-center text-center md:flex-row md:text-left">
        <Illustration
          v-if="props.vignette"
          data-test="stash-empty-state|illustration"
          type="vignette"
          class="mx-3"
          :name="props.vignette"
          :size="props.vignetteSize"
        />
        <section>
          <slot name="title">
            <h3 class="mb-1.5">
              {{ props.title }}
            </h3>
          </slot>

          <slot name="subtitle">
            <p v-if="props.subtitle || slots.subtitle" :class="{ 'mb-8': slots.button, 'mb-0': !slots.button }">
              {{ props.subtitle }}
            </p>
          </slot>

          <slot name="button"></slot>
          <slot name="footnote">
            <p v-if="props.footnote" class="mb-0 text-xs" :class="{ 'mt-3': slots.button }">
              {{ props.footnote }}
            </p>
          </slot>
        </section>
      </div>
    </template>

    <template v-else>
      <div
        class="mx-auto my-0 flex items-center justify-center py-12"
        :class="[classes.root, { 'text-center': !props.image.src && !slots.image }]"
      >
        <slot name="image">
          <img
            v-if="props.image.src"
            class="mr-6"
            :alt="props.image.alt"
            :class="classes.image"
            :src="props.image.src"
            data-test="stash-empty-state__img"
          />
        </slot>

        {{ props.text || t('ll.emptyState.noResults') }}
      </div>
    </template>
  </div>
</template>

<style module>
  .root {
    max-width: 600px;
  }

  .image {
    max-width: 60px;
  }
</style>
